// FIN-1303: the one mapping from the four permissions a tax connect asks for to the role names the
// account stores in `granted_roles`, shared by the client app's TaxConnectSheet and src/'s
// TaxAccountSection (src/ takes it through the `@financialharmony/ui/tax/permissionState` subpath,
// like `grantStage`). Pure, so neither app needs the other's components.

/** The four permissions one signature grants, in the order the sheet lists them. */
export const TAX_PERMISSION_IDS = ["tax_account", "vat", "employer_declarations", "income_tax"] as const;

export type TaxPermissionId = (typeof TAX_PERMISSION_IDS)[number];

/**
 * Each permission's role as stored in `granted_roles`: `SKATTEVERKET_ROLES` in
 * supabase/functions/_shared/types/tax/skatteverket/api.ts. The backend has already translated
 * Skatteverket's own codes (skol, momsdeklaration_ombud, esro, efink) into these names before
 * storing, so a raw authority code is never matched here.
 */
export const TAX_PERMISSION_ROLE: Record<TaxPermissionId, string> = {
  tax_account: "skattekonto",
  vat: "moms",
  employer_declarations: "arbetsgivardeklaration",
  income_tax: "inkomstdeklaration",
};

/**
 * - `granted`: the roles lookup lists the role as live.
 * - `missing`: the lookup answered and the role is not in it.
 * - `unknown`: the lookup has not answered (or this screen does not know yet): no claim either way.
 */
export type TaxPermissionState = "granted" | "missing" | "unknown";

/**
 * The state of one permission. `grantedRoles` null/undefined means the lookup never answered;
 * an empty list means it answered and nothing is granted. A role we do not ask for is ignored,
 * so an unknown future role can never show as a check. A role signed but not yet live is not in
 * the list the authority returns as granted, so it is never `granted` here.
 */
export function taxPermissionState(
  permission: TaxPermissionId,
  grantedRoles: readonly string[] | null | undefined,
): TaxPermissionState {
  if (grantedRoles == null) return "unknown";
  return grantedRoles.includes(TAX_PERMISSION_ROLE[permission]) ? "granted" : "missing";
}

/**
 * The granted roles of a client-app account view, where proto3 cannot carry null beside an empty
 * list and `grantedRolesKnown` carries the distinction.
 */
export function knownGrantedRoles(account: {
  grantedRoles?: string[];
  grantedRolesKnown?: boolean;
}): string[] | null {
  return account.grantedRolesKnown ? (account.grantedRoles ?? []) : null;
}
